import { useRef, useState } from "react";
import {
  CheckCircle2,
  FileText,
  Loader2,
  Paperclip,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import type {
  ClaimDocument,
  ClaimLineCreateRequest,
  ExpenseClaimLine,
} from "../types";
import { useDeleteClaimDocument, useUploadClaimDocument } from "../hooks";
import { ClaimDocumentViewer } from "./ClaimDocumentViewer";
import { Button } from "#/components/common/Button";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtBytes(n?: number | null) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

// ─── attached doc strip (on existing lines) ───────────────────────────────────

function LineDocumentRow({
  claimId,
  lineId,
  docs,
  editable,
}: {
  claimId: number;
  lineId: number;
  docs: ClaimDocument[];
  editable: boolean;
}) {
  const upload   = useUploadClaimDocument(claimId);
  const remove   = useDeleteClaimDocument(claimId);
  const inputRef = useRef<HTMLInputElement>(null);

  // Only show docs that explicitly belong to this line (lineId set by backend)
  // OR unassigned docs — the parent decides which docs array to pass.
  const hasDoc = docs.length > 0;

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await upload.mutateAsync({ file, documentType: "LINE_RECEIPT", lineId });
    } catch { /* surfaced via upload.isError */ }
    e.target.value = "";
  }

  return (
    <div className="mt-2 space-y-1.5">
      {docs.map((d) => (
        <div key={d.id} className="flex items-center gap-2 rounded-xl border bg-slate-50 px-3 py-2">
          <Paperclip className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-700">
            {d.originalFileName}
          </span>
          {d.fileSizeBytes && (
            <span className="shrink-0 text-[10px] text-slate-400">{fmtBytes(d.fileSizeBytes)}</span>
          )}
          <ClaimDocumentViewer claimId={claimId} doc={d} compact />
          {editable && (
            <button
              type="button"
              onClick={() => remove.mutate(d.id)}
              disabled={remove.isPending}
              className="shrink-0 rounded-lg p-1 text-red-500 hover:bg-red-50 disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}

      {editable && (
        <label
          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-bold transition-colors ${
            hasDoc
              ? "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
              : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
          }`}
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <UploadCloud className="h-3.5 w-3.5" />
          )}
          {upload.isPending ? "Uploading…" : hasDoc ? "Replace / Add" : "Attach receipt *"}
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={handleFile}
            disabled={upload.isPending}
          />
        </label>
      )}

      {upload.isError && (
        <p className="text-[10px] font-semibold text-red-600">
          {(upload.error as { response?: { status?: number } })?.response?.status === 409
            ? "A document is already attached. Remove it first."
            : "Upload failed — please try again."}
        </p>
      )}

      {!editable && !hasDoc && (
        <p className="text-[10px] italic text-slate-400">No document attached</p>
      )}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

type Props = {
  claimId: number;
  lines: ExpenseClaimLine[];
  documents: ClaimDocument[];
  loading?: boolean;
  editable?: boolean;
  /** Must resolve to the created ExpenseClaimLine so we can upload the file with its id */
  onAdd: (payload: ClaimLineCreateRequest) => Promise<ExpenseClaimLine>;
  onDelete: (lineId: number) => Promise<unknown>;
};

export function ClaimLineEditor({
  claimId,
  lines,
  documents,
  loading,
  editable = true,
  onAdd,
  onDelete,
}: Props) {
  // ── add-line form state ──────────────────────────────────────────────────
  const [expenseDate,  setExpenseDate]  = useState("");
  const [description,  setDescription]  = useState("");
  const [amount,       setAmount]       = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formError,    setFormError]    = useState("");
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const upload = useUploadClaimDocument(claimId);
  const total  = lines.reduce((s, l) => s + Number(l.amount ?? 0), 0);

  // Docs that already have a lineId set by the backend
  const backendSupportsLineId = documents.some((d) => d.lineId != null);

  function docsForLine(lineId: number, idx: number) {
    if (backendSupportsLineId) {
      return documents.filter((d) => d.lineId === lineId);
    }
    // Backend fallback: show all unassigned docs on the first line only
    return idx === 0 ? documents : [];
  }

  const linesWithoutDoc = backendSupportsLineId
    ? lines.filter((l) => !documents.some((d) => d.lineId === l.id))
    : [];
  const allComplete = linesWithoutDoc.length === 0;

  // ── submit handler ───────────────────────────────────────────────────────
  async function handleAdd(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError("");
    setUploadError("");

    const num   = Number(amount);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (!expenseDate)                     { setFormError("Expense date is required."); return; }
    if (new Date(expenseDate) > today)    { setFormError("Expense date cannot be in the future."); return; }
    if (!description.trim())              { setFormError("Description is required."); return; }
    if (!Number.isFinite(num) || num <= 0){ setFormError("Amount must be greater than 0."); return; }
    if (!selectedFile)                    { setFormError("Please attach a receipt before adding the line."); return; }

    // 1. Create the line
    let newLine: ExpenseClaimLine;
    try {
      newLine = await onAdd({ expenseDate, description: description.trim(), amount: num });
    } catch {
      setFormError("Failed to add line — please try again.");
      return;
    }

    // 2. Upload the receipt tied to the new lineId
    setUploading(true);
    try {
      await upload.mutateAsync({
        file: selectedFile,
        documentType: "LINE_RECEIPT",
        lineId: newLine.id,
      });
    } catch {
      setUploadError(
        `Line was added but the receipt upload failed. Use "Attach receipt" on the line below to retry.`
      );
    } finally {
      setUploading(false);
    }

    // Reset form
    setExpenseDate("");
    setDescription("");
    setAmount("");
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const isBusy = loading || uploading;

  return (
    <section className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
      {/* ── Header ── */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Claim lines</h2>
          <p className="text-sm text-slate-500">
            Fill in each expense and attach its receipt before adding.
          </p>
        </div>
        <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">
          Total: KES {total.toLocaleString()}
        </div>
      </div>

      {/* ── Add-line form ── */}
      {editable && (
        <form
          onSubmit={handleAdd}
          className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3"
        >
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">New line</p>

          {/* Row 1: date · description · amount */}
          <div className="grid gap-3 md:grid-cols-[180px_1fr_160px]">
            <input
              type="date"
              value={expenseDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setExpenseDate(e.target.value)}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Expense description"
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
            <input
              type="number"
              min="1"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount (KES)"
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          {/* Row 2: file picker + submit */}
          <div className="flex flex-wrap items-center gap-3">
            {/* File picker */}
            <label
              className={`inline-flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold transition-colors ${
                selectedFile
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
              }`}
            >
              {selectedFile ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <UploadCloud className="h-3.5 w-3.5 shrink-0" />
              )}
              <span className="max-w-[180px] truncate">
                {selectedFile ? selectedFile.name : "Attach receipt *"}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </label>

            {/* Clear file */}
            {selectedFile && (
              <button
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="rounded-lg p-1 text-slate-400 hover:text-red-500"
              >
                <X className="h-4 w-4" />
              </button>
            )}

            <div className="ml-auto">
              <Button type="submit" loading={isBusy} disabled={isBusy}>
                {uploading ? "Uploading receipt…" : loading ? "Adding…" : "Add line"}
              </Button>
            </div>
          </div>

          {/* Form errors */}
          {formError && (
            <p className="text-xs font-semibold text-red-600">{formError}</p>
          )}
          {uploadError && (
            <p className="text-xs font-semibold text-amber-700">{uploadError}</p>
          )}
        </form>
      )}

      {/* ── Lines list ── */}
      {lines.length === 0 ? (
        <p className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
          No claim lines added yet.
        </p>
      ) : (
        <div className="space-y-3">
          {lines.map((line, idx) => {
            const lineDocs = docsForLine(line.id, idx);
            const hasDoc   = lineDocs.length > 0;
            return (
              <div
                key={line.id}
                className={`rounded-2xl border p-4 transition-colors ${
                  !hasDoc && editable ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Index badge */}
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-700">
                    {idx + 1}
                  </div>

                  <div className="min-w-0 flex-1">
                    {/* Line header */}
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-bold text-slate-800">{line.description}</p>
                        <p className="text-xs text-slate-400">{line.expenseDate}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        <span className="font-black text-emerald-700">
                          KES {Number(line.amount ?? 0).toLocaleString()}
                        </span>
                        {editable && (
                          <button
                            type="button"
                            onClick={() => onDelete(line.id)}
                            className="rounded-lg p-1 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Attached docs for this line */}
                    <LineDocumentRow
                      claimId={claimId}
                      lineId={line.id}
                      docs={lineDocs}
                      editable={editable}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Completion hint (only when backend supports lineId) ── */}
      {editable && backendSupportsLineId && lines.length > 0 && !allComplete && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <FileText className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-700">
            <strong>{linesWithoutDoc.length}</strong> line{linesWithoutDoc.length !== 1 ? "s" : ""} still need a supporting document before you can submit.
          </p>
        </div>
      )}
    </section>
  );
}
